/* eslint-disable react/prop-types */
/* eslint-disable react-native/no-inline-styles */
/*
Author: Gilang Romadhanu Tartila
*/

import React, { useEffect, useMemo } from 'react';
import { useDispatch } from 'react-redux';
import { View } from 'react-native';

import Button from '@components/form/Button';
import SociollaText from '@components/sociolla/SociollaText';
import Table from '@components/table/Table';
import TableMobile from '@components/table/TableMobile';
import MoreButton from '@components/navbar/MoreButton';
import NotificationSettingButton from '@components/navbar/NotificationSettingButton';
import * as Layout from '@components/screen/Layout';

import Colors from '@global/colors';
import { showPopup } from '@/store/modal';
import {
  hasNoAccess,
  convertMarketTypeData,
  exportCsvApi,
} from '@global/helpers';
import { isWeb } from '@global/screens';

import useListScreen from '@/hooks/useListScreen';
import {
  useGetMarketBusinessTypeListQuery,
  useGetMarketBusinessTypeCountQuery,
  useGetMarketBusinessTypeInfiniteListInfiniteQuery,
} from '@api/listApi';

const EDIT_ROLES_BLOCKLIST = [
  'regional_manager',
  'regional_support',
  'key_account_manager',
  'group_key_account_manager',
  'national_key_account_manager',
  'distributor_manager',
  'general_manager',
  'vice_president',
  'regional_distributor_manager',
];

const tableStructure = {
  code: {
    head: 'Market Type',
    body: 'code',
    textStyle: { fontFamily: 'figtree-medium' },
  },
  business_type: {
    head: 'Business Type',
    body: 'business_type',
    type: 'slot',
  },
  customer_payment_terms: {
    head: 'Payment Terms',
    body: 'customer_payment_terms',
  },
  customer_limit_amount: {
    head: 'Limit Amount',
    body: 'customer_limit_amount',
    type: 'currency',
  },
  customer_cluster: {
    head: 'Cluster',
    body: 'customer_cluster',
  },
  total_products_premium: {
    head: 'Premium Products',
    body: 'total_products_premium',
  },
  total_products_non_premium: {
    head: 'Non-Premium Products',
    body: 'total_products_non_premium',
  },
  is_active: {
    head: 'Active/Inactive',
    body: 'business_type_is_active',
    type: 'status_active',
  },
};

// Search box writes `code` (→ $regex).
const toApiFilter = (raw = {}) => {
  const filter = {};
  const code = raw.code?.$regex ?? raw.code;
  if (code) {
    filter.code = { $regex: code, $options: 'i' };
  }
  return filter;
};

const MarketBusinessTypeScreen = (props) => {
  const dispatch = useDispatch();

  const { list, count, openChild, scope, params } = useListScreen({
    listHook: useGetMarketBusinessTypeListQuery,
    countHook: useGetMarketBusinessTypeCountQuery,
    infiniteHook: useGetMarketBusinessTypeInfiniteListInfiniteQuery,
    toFilter: toApiFilter,
  });

  const hasAccess = hasNoAccess(EDIT_ROLES_BLOCKLIST);

  const searchValue = useMemo(() => {
    const k = params.filter?.code;
    return k && k.$regex ? k.$regex : k ?? '';
  }, [params.filter]);

  // canViewDetail: edit-access OR distributor_manager (view-only detail) — uat.
  const canViewDetail = hasAccess || !hasNoAccess(['distributor_manager']);

  // Flatten each market type into one row per active business type
  // (legacy convertMarketTypeData). Several rows share the same market-type
  // `id`, so give each a unique `id` for list keys and keep the market-type id
  // under `market_type_id` for the edit action.
  const rows = useMemo(() => {
    const raw = isWeb
      ? Array.isArray(list.data)
        ? list.data
        : []
      : (list.data?.pages ?? []).flat();
    return convertMarketTypeData(raw).map((row) => ({
      ...row,
      market_type_id: row.id,
      id: `${row.id}-${row.business_type_id}`,
    }));
  }, [list.data]);

  const onEdit = ({ data }) => {
    // canViewDetail: edit-access OR distributor_manager (view-only detail) — uat.
    if (!canViewDetail) {
      return;
    }
    openChild('MarketBusinessTypeFormScreen', {
      id: data.market_type_id,
      businessId: data.business_type_id,
      titleScreen: data.code,
    });
  };

  const onAdd = () => openChild('MarketBusinessTypeFormScreen');

  const onExport = async () => {
    dispatch(showPopup({ name: 'isLoading', value: true }));
    try {
      await exportCsvApi(
        'market-type',
        params.filter ? { ...params.filter } : {},
        {
          isDownload: false,
          message: 'Export in progress, the result will be sent to your email.',
        },
      );
      dispatch(showPopup({ name: 'isLoading', value: false }));
    } catch (error) {
      dispatch(showPopup({ name: 'isLoading', value: false }));
    }
  };

  // Own the mobile header: Export + (Add through openChild so the list is
  // preserved on return). Export reads the scoped params directly.
  useEffect(() => {
    props.navigation.setOptions({
      headerRight: () => {
        if (isWeb) {
          return <NotificationSettingButton {...props} />;
        }
        const menus = [
          { type: 'function', name: 'Export', callback: onExport },
        ];
        if (hasAccess) {
          menus.unshift({ type: 'function', name: 'Add', callback: onAdd });
        }
        return <MoreButton menu={menus} {...props} />;
      },
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [props.navigation, hasAccess, params.filter]);

  return (
    <Layout.Container>
      {isWeb ? (
        <>
          <Layout.Title title="Market & Business Type">
            <View style={{ flexDirection: 'row' }}>
              <Button
                type="outline-primary"
                rounded={true}
                buttonStyle={{ width: 100, marginRight: hasAccess ? 15 : 0 }}
                title={
                  <SociollaText
                    type="medium"
                    style={{ fontSize: 14, color: Colors.blue1 }}>
                    Export
                  </SociollaText>
                }
                onPress={onExport}
              />
              {hasAccess ? (
                <Button
                  type="primary"
                  rounded={true}
                  buttonStyle={{ width: 100 }}
                  title={
                    <SociollaText
                      type="medium"
                      style={{ fontSize: 14, color: Colors.white }}>
                      + Add
                    </SociollaText>
                  }
                  onPress={onAdd}
                />
              ) : (
                false
              )}
            </View>
          </Layout.Title>

          <Table
            filterName={scope}
            searchKey="code"
            searchPlaceholder="Search by market type"
            structures={tableStructure}
            data={rows}
            loading={list.isLoading}
            refetching={list.isFetching}
            hasNoHide={true}
            totalData={count.data}
            skip={params.skip}
            limit={params.limit}
            hasCallback={true}
            showFilter={false}
            onClickRow={(val) => onEdit(val)}
            searchValue={searchValue}
            slot={{
              business_type: ({ data, index }) => (
                <SociollaText key={index} style={{ fontSize: 12 }}>{`${
                  data.business_type ? data.business_type : '-'
                } (${
                  data.business_type_description
                    ? data.business_type_description
                    : '-'
                })`}</SociollaText>
              ),
            }}
          />
        </>
      ) : (
        <TableMobile
          filterName={scope}
          searchKey="code"
          placeholder="Search by market type"
          loading={list.isLoading}
          refetching={list.isFetching}
          loadingLoadMore={list.isFetchingNextPage}
          hasNextPage={list.hasNextPage}
          onLoadMore={list.fetchNextPage}
          onRefresh={list.refetch}
          skip={params.skip}
          limit={params.limit}
          data={rows}
          totalData={count.data}
          searchValue={searchValue}
          renderItem={({ data, index }) => (
            <Layout.ItemMobile
              key={index}
              touchable={true}
              column={true}
              onPress={() => onEdit({ data })}>
              <SociollaText
                type="medium"
                style={{ width: '100%', marginBottom: 4 }}>
                {data.code}
              </SociollaText>
              <SociollaText
                style={{
                  width: '100%',
                  fontSize: 12,
                }}>{`${data.business_type} (${data.business_type_description})`}</SociollaText>
            </Layout.ItemMobile>
          )}
        />
      )}
    </Layout.Container>
  );
};

export default MarketBusinessTypeScreen;
